/**
 * TikTok browser tracking helper for funnel pages.
 * It loads TikTok only when the selected Conversion API belongs to TikTok.
 * It also sends ViewContent / SubmitForm events for browser/server dedupe.
 */

import {
  CONVERSION_API_PLATFORM,
  TIKTOK_PIXEL_DEFAULT_ID,
  TIKTOK_SUBMIT_FORM_EVENT_NAME,
  TIKTOK_VIEW_CONTENT_EVENT_NAME
} from "../templates/strings";

// Resolve TikTok pixel from the new Conversion API field or old TikTok field.
export const getTikTokPixelId = (funnel) => {
  const conversionApi = funnel?.general?.conversionApi;
  const isTikTokConversionApi =
    conversionApi?.platform === CONVERSION_API_PLATFORM.TIKTOK ||
    conversionApi?._id === TIKTOK_PIXEL_DEFAULT_ID;
  if (conversionApi?._id) {
    return isTikTokConversionApi ? conversionApi._id : null;
  }
  return funnel?.general?.tiktokConversionApi?._id || TIKTOK_PIXEL_DEFAULT_ID;
};

// Create TikTok browser pixel only once in the page.
export const initTikTokPixel = (pixelId = TIKTOK_PIXEL_DEFAULT_ID) => {
  if (typeof window === "undefined" || window.__tiktokPixelLoaded) {
    return;
  }

  // Load the selected TikTok browser pixel once for this funnel page.
  (function (windowObj, documentObj, queueName) {
    // windowObj = window, documentObj = document, queueName = "ttq".
    windowObj.TiktokAnalyticsObject = queueName;
    // ttq is TikTok's browser queue before the real script is downloaded.
    const ttq = (windowObj[queueName] = windowObj[queueName] || []);
    ttq.methods = ["track"];
    // Create queue methods so tracking calls made early are not lost.
    ttq.setAndDefer = function (target, method) {
      // Queue TikTok calls until the real pixel script is ready.
      target[method] = function () {
        target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i += 1) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    ttq.load = function (id) {
      // ttq.load adds TikTok's script tag and stores pixel setup data.
      const script = documentObj.createElement("script");
      const tiktokPixelEventTrackingScriptUrl = `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${id}&lib=${queueName}`;
      script.type = "text/javascript";
      script.async = true;
      script.src = tiktokPixelEventTrackingScriptUrl;
      const firstScript = documentObj.getElementsByTagName("script")[0];
      firstScript.parentNode.insertBefore(script, firstScript);
      // TikTok uses these internal objects to connect events with the pixel id.
      ttq._i = ttq._i || {};
      ttq._i[id] = [];
      ttq._i[id]._u = script.src;
      ttq._t = ttq._t || {};
      ttq._t[id] = Date.now();
      ttq._o = ttq._o || {};
      ttq._o[id] = {};
    };
    ttq.load(pixelId);
  })(window, document, "ttq");

  // Remember that the TikTok pixel script was already added.
  window.__tiktokPixelLoaded = pixelId;
};

// Fire TikTok ViewContent when the funnel page is loaded.
export const trackTikTokViewContent = (funnel) => {
  const pixelId = getTikTokPixelId(funnel);
  if (!pixelId) {
    return;
  }
  initTikTokPixel(pixelId);
  window.ttq?.track(TIKTOK_VIEW_CONTENT_EVENT_NAME);
};

// Fire TikTok SubmitForm after the lead is saved.
export const trackTikTokSubmitForm = (eventId, funnel) => {
  const pixelId = getTikTokPixelId(funnel);
  if (!eventId || !pixelId) {
    return;
  }
  initTikTokPixel(pixelId);
  // Use the saved lead id so browser and server events can deduplicate.
  window.ttq?.track(
    TIKTOK_SUBMIT_FORM_EVENT_NAME,
    {},
    { event_id: eventId.toString() }
  );
};
